/**
 * What the WhatsApp row and its connect modal decide, as pure functions.
 *
 * The components stay thin: which row variant a brand sees, how often to poll
 * a link intent, what the countdown reads, and which sentence each intent
 * status gets - all live here so each edge is stated once and asserted.
 *
 * Fail-closed throughout: no response, an error, or a feature that is off all
 * read as "hidden" or "not connected". Nothing here ever reports a connection
 * the server did not confirm.
 *
 * @module web/screens/app/overview/customerChannels
 */
import type {
  ChannelConnectionView,
  CustomerChannelsResponse,
  LinkIntentStatus,
  LinkIntentStatusResponse,
} from "../../../api/types.ts";

/**
 * Which WhatsApp row a brand sees.
 *
 * The order is the rule:
 *   1. an active connection is shown even if the feature has since been
 *      switched off - the person can still see and revoke what they linked
 *   2. feature off (and nothing linked) → no row at all
 *   3. a brand-token session has no person to bind a chat to
 *   4. WhatsApp without Twilio config is "not available yet", not an offer
 */
export type ChannelRowState =
  | { kind: "hidden" }
  | { kind: "personal_needed" }
  | { kind: "unavailable" }
  | { kind: "disconnected" }
  | {
      kind: "connected";
      connection: ChannelConnectionView;
      /**
       * False while card-ready sending is not armed on the server (the
       * notification flag or the approved template is missing). The row then
       * says "connected, sending follows" instead of promising every finished
       * video — a promise nothing would keep.
       */
      sends: boolean;
    };

export function channelRowState(resp: CustomerChannelsResponse | null | undefined): ChannelRowState {
  if (!resp) return { kind: "hidden" };
  const connection = (resp.connections ?? []).find(c => c.provider === "whatsapp" && c.status === "active");
  if (connection) return { kind: "connected", connection, sends: resp.cardDelivery === true };
  if (!resp.enabled) return { kind: "hidden" };
  if (!resp.personal) return { kind: "personal_needed" };
  if (!resp.providers?.whatsapp?.configured) return { kind: "unavailable" };
  return { kind: "disconnected" };
}

/** How long to wait before the next status poll, or null when there is nothing left to wait for. */
export function nextPollDelay(status: LinkIntentStatus | null | undefined): number | null {
  return status === "issued" || status === "pending" ? 3000 : null;
}

/** "m:ss" until `expiresAt`, clamped at 0:00. An unparseable date reads as expired. */
export function formatCountdown(expiresAt: string, now: number): string {
  const ms = Date.parse(expiresAt) - now;
  const total = Number.isFinite(ms) && ms > 0 ? Math.ceil(ms / 1000) : 0;
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s < 10 ? "0" : ""}${s}`;
}

export interface IntentView {
  title: string;
  body: string;
  /** The one loud button, when there is one. */
  primary?: "open" | "confirm" | "new_code";
  /** The quiet way out, when leaving needs saying. */
  secondary?: "cancel";
}

/**
 * The words for each intent status.
 *
 * An error never changes the buttons: the screen stays where it was, the error
 * sentence replaces the body, and nothing reads as "connected" unless the
 * server said `confirmed`.
 */
export function intentView(intent: LinkIntentStatusResponse | null, error: string | null): IntentView {
  if (!intent) {
    return error
      ? { title: "Connect WhatsApp", body: error, primary: "new_code" }
      : { title: "Connect WhatsApp", body: "Getting your code…" };
  }
  const view = viewFor(intent);
  return error ? { ...view, body: error } : view;
}

function viewFor(intent: LinkIntentStatusResponse): IntentView {
  switch (intent.status) {
    case "issued":
      return {
        title: "Connect WhatsApp",
        body: "Send the pre-filled message from your own WhatsApp.",
        primary: "open",
      };
    case "pending":
      return {
        title: `Is this your WhatsApp ${intent.senderMasked ?? ""}?`.replace(/\s+\?$/, "?"),
        body: "Confirm and you'll get finished videos and updates there.",
        primary: "confirm",
        secondary: "cancel",
      };
    case "confirmed":
      return { title: "Connected.", body: "Finished videos and updates now reach your WhatsApp." };
    case "expired":
      return { title: "Code expired.", body: "Codes last 10 minutes. Get a new one and send it again.", primary: "new_code" };
    case "canceled":
      return { title: "Code canceled.", body: "A newer code replaced this one.", primary: "new_code" };
  }
}

/** The label on a primary button. */
export const PRIMARY_LABEL: Record<NonNullable<IntentView["primary"]>, string> = {
  open: "Open WhatsApp",
  confirm: "Connect",
  new_code: "New code",
};

/**
 * One sentence for an API failure, by the server's error code.
 *
 * Codes are the contract in customer-chat-routes; the status is the fallback
 * when a code is missing. A 401 here means "no person", not "session gone":
 * a brand-token session is signed in, just not as someone.
 */
export function errorSentence(status: number, code: string | null | undefined, fallback?: string | null): string {
  switch (code) {
    case "personal_login_required": return "Sign in with Google first to link your WhatsApp.";
    case "not_a_member": return "You're not a member of this brand.";
    case "pilot_disabled": return "WhatsApp isn't switched on right now.";
    case "csrf": return "Please reload the page and try again.";
    case "not_configured": return "WhatsApp isn't available yet.";
    case "rate_limited": return "Too many codes requested. Try again in a few minutes.";
    case "not_pending": return "That code is no longer waiting to be confirmed.";
    case "sender_taken": return "This WhatsApp number is already linked to another account.";
  }
  if (status === 404) return "This code is no longer valid. Get a new one.";
  if (status === 401 || status === 403) return "You're not allowed to do that here.";
  if (status === 0) return "No connection. Check your internet and try again.";
  return fallback && !/^HTTP \d+$/.test(fallback) ? fallback : "Something went wrong. Please try again.";
}
